import { CircularProgress, Grid } from '@material-ui/core';
import { Card, Icons, Typography } from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';

import type { PreviousReportFiles } from '../../rxdb/previousReports/previousReports.types';
import { YYYYMMDD_DATE_FORMAT } from '../../utilities/constants';
import { getImage } from '../../utilities/storageUtils';
import { useNotification } from '../../utilities/useNotification';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { downloadURI } from '../../utilities/utilityFunctions';
import OfflineDialog from '../OfflineDialog';
import useStyles from './PreviousReportDetails.styles';

interface Props {
  report: PreviousReportFiles;
}
const PreviousReportDetails: React.FC<Props> = ({ report }) => {
  const classes = useStyles();
  const online = useOnlineStatus();
  const { errorNotification } = useNotification();

  const [isReportDownloading, setIsReportDownloading] = useState<boolean>(false);
  const [isOffline, setIsOffline] = useState<boolean>(false);

  const downloadPreviousReportFiles = async (filePath: string, fileName: string) => {
    if (online) {
      setIsOffline(false);
      setIsReportDownloading(true);
      try {
        const slicedFilePath = filePath.charAt(0) === '/' ? filePath.slice(1) : filePath;
        const result = await getImage(slicedFilePath);
        const blobPath = await fetch(String(result))
          .then(r => r.blob())
          .then(e => {
            return e;
          });
        const path = URL.createObjectURL(blobPath);
        downloadURI(path, fileName);
      } catch (error) {
        errorNotification('Error while downloading report please try again');
      } finally {
        setIsReportDownloading(false);
      }
    } else {
      setIsOffline(true);
    }
  };

  return (
    <>
      <Card elevation={1} className={classes.card}>
        <div
          onClick={() => downloadPreviousReportFiles(report.originalPath, report.assetName)}
          className={classes.cursor}
          data-testid="previous-report-download"
        >
          <Grid item container xs={12} alignItems="center">
            <Grid item xs={9} className={classes.grid}>
              <Typography
                color="primary"
                fontWeight={300}
                className="assetName"
                data-testid="previous-report-asset-name"
              >
                {report.assetName}
              </Typography>
              <Typography className="hypen">-</Typography>
              {report.inspectionType}
              <span className="inspectionIdGrid" />
              {report.pkAssetInspection}
            </Grid>
            <Grid item xs={3}>
              <Grid container justifyContent="flex-end" alignItems="center">
                <Typography variant="subtitle1" className="dateGrid">
                  {dayjs(report.dateCompleted).format(`${YYYYMMDD_DATE_FORMAT}`)}
                </Typography>
                {!isReportDownloading ? (
                  <Icons.DownloadFeather color="primary" />
                ) : (
                  <CircularProgress className={classes.primaryColor} />
                )}
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Card>
      {isOffline && <OfflineDialog open={isOffline} onClose={() => setIsOffline(false)} />}
    </>
  );
};

export default observer(PreviousReportDetails);
